import type { InstallExceptionDisposition } from './install-exception.js';

export const INSTALL_STATUSES = ['installed', 'not_installed', 'not_yet_recorded'] as const;
export type InstallStatus = (typeof INSTALL_STATUSES)[number];

export const INSTALL_OUTCOMES = [
  'installed',
  'left_with_manager',
  'removed_from_store',
  'submitted_with_zero_units',
] as const;
export type InstallOutcome = (typeof INSTALL_OUTCOMES)[number];

export const INSTALL_STATUS_VIEWS = ['not_installed', 'installed', 'not_yet_recorded', 'all'] as const;
export type InstallStatusView = (typeof INSTALL_STATUS_VIEWS)[number];

export type InstallStatusClassification = {
  status: InstallStatus;
  outcome: InstallOutcome | null;
};

export type InstallStatusRow = {
  taskId: number;
  storeId: number;
  storeNo: string | null;
  storeName: string;
  campaignId: number;
  campaignName: string;
  photoRequest: boolean;
  status: InstallStatus;
  outcome: InstallOutcome | null;
  reason: string | null;
  note: string | null;
  operatorName: string | null;
  plannedQty: number;
  postedUnits: number | null;
  recordedAt: string | null;
};

export type InstallStatusReasonCount = {
  reason: string;
  leftWithManager: number;
  removedFromStore: number;
};

export type InstallStatusSummary = {
  planned: number;
  stores: number;
  submitted: number;
  installed: number;
  leftWithManager: number;
  removedFromStore: number;
  submittedWithZeroUnits: number;
  notYetRecorded: number;
  notInstalled: number;
  photoRequestNotInstalled: number;
  realLeftWithManager: number;
  realRemovedFromStore: number;
  reasons: InstallStatusReasonCount[];
};

export type InstallStatusCampaignGroup = {
  campaignId: number;
  campaignName: string;
  photoRequest: boolean;
  planned: number;
  installed: number;
  leftWithManager: number;
  removedFromStore: number;
  submittedWithZeroUnits: number;
  notYetRecorded: number;
};

export type InstallStatusStoreGroup = {
  storeId: number;
  storeNo: string | null;
  storeName: string;
  planned: number;
  installed: number;
  leftWithManager: number;
  removedFromStore: number;
  submittedWithZeroUnits: number;
  notYetRecorded: number;
  operators: string;
};

type InstallStatusBucket = {
  planned: number;
  installed: number;
  leftWithManager: number;
  removedFromStore: number;
  submittedWithZeroUnits: number;
  notYetRecorded: number;
};

/**
 * Latest posting at the task's current reset generation. A later submission
 * without an install exception clears the exception, matching compliance.
 */
export function selectLatestSubmission<T extends { id: number; resetGeneration: number }>(
  events: readonly T[],
  resetCount: number,
): T | null {
  let latest: T | null = null;
  for (const event of events) {
    if (event.resetGeneration !== resetCount) continue;
    if (!latest || event.id > latest.id) latest = event;
  }
  return latest;
}

/**
 * One posting task. An install exception wins over posted units, including a
 * left-with-manager row that still has units recorded on the posting.
 */
export function classifyInstallStatus(input: {
  hasSubmission: boolean;
  postedUnits: number | null;
  disposition: InstallExceptionDisposition | null;
}): InstallStatusClassification {
  if (!input.hasSubmission) {
    return { status: 'not_yet_recorded', outcome: null };
  }
  if (input.disposition) {
    switch (input.disposition) {
      case 'left_with_manager':
        return { status: 'not_installed', outcome: 'left_with_manager' };
      case 'removed_from_store':
        return { status: 'not_installed', outcome: 'removed_from_store' };
      default: {
        const unreachable: never = input.disposition;
        return unreachable;
      }
    }
  }
  if ((input.postedUnits ?? 0) > 0) {
    return { status: 'installed', outcome: 'installed' };
  }
  return { status: 'not_installed', outcome: 'submitted_with_zero_units' };
}

export function isPhotoRequestCampaign(campaignName: string): boolean {
  return campaignName.toUpperCase().includes('PHOTO REQUEST');
}

export function installStatusLabel(status: InstallStatus): string {
  switch (status) {
    case 'installed':
      return 'Installed';
    case 'not_installed':
      return 'Not installed';
    case 'not_yet_recorded':
      return 'Not yet recorded';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

export function installOutcomeLabel(outcome: InstallOutcome | null): string {
  switch (outcome) {
    case 'installed':
      return 'Installed';
    case 'left_with_manager':
      return 'Left with manager';
    case 'removed_from_store':
      return 'Removed from store';
    case 'submitted_with_zero_units':
      return 'Submitted with zero units';
    case null:
      return '';
    default: {
      const unreachable: never = outcome;
      return unreachable;
    }
  }
}

export function installStatusViewLabel(view: InstallStatusView): string {
  switch (view) {
    case 'not_installed':
      return 'Not installed';
    case 'installed':
      return 'Installed';
    case 'not_yet_recorded':
      return 'Not yet recorded';
    case 'all':
      return 'All posting tasks';
    default: {
      const unreachable: never = view;
      return unreachable;
    }
  }
}

export function formatInstallStatusCount(value: number): string {
  return value.toLocaleString('en-IE');
}

export function formatInstallStatusPercent(part: number, whole: number): string | null {
  if (whole <= 0) return null;
  return `${((part / whole) * 100).toFixed(1)}%`;
}

function emptyBucket(): InstallStatusBucket {
  return {
    planned: 0,
    installed: 0,
    leftWithManager: 0,
    removedFromStore: 0,
    submittedWithZeroUnits: 0,
    notYetRecorded: 0,
  };
}

function addToBucket(bucket: InstallStatusBucket, row: InstallStatusRow): void {
  bucket.planned += 1;
  switch (row.status) {
    case 'installed':
      bucket.installed += 1;
      return;
    case 'not_yet_recorded':
      bucket.notYetRecorded += 1;
      return;
    case 'not_installed':
      switch (row.outcome) {
        case 'left_with_manager':
          bucket.leftWithManager += 1;
          return;
        case 'removed_from_store':
          bucket.removedFromStore += 1;
          return;
        case 'submitted_with_zero_units':
          bucket.submittedWithZeroUnits += 1;
          return;
        case 'installed':
        case null:
          throw new Error(`Not-installed task ${row.taskId} has outcome ${String(row.outcome)}`);
        default: {
          const unreachable: never = row.outcome;
          throw new Error(`Unhandled install outcome ${String(unreachable)}`);
        }
      }
    default: {
      const unreachable: never = row.status;
      throw new Error(`Unhandled install status ${String(unreachable)}`);
    }
  }
}

export function summarizeInstallStatus(rows: readonly InstallStatusRow[]): InstallStatusSummary {
  const bucket = emptyBucket();
  const storeIds = new Set<number>();
  let photoRequestNotInstalled = 0;
  let realLeftWithManager = 0;
  let realRemovedFromStore = 0;
  const reasons = new Map<string, InstallStatusReasonCount>();

  for (const row of rows) {
    addToBucket(bucket, row);
    storeIds.add(row.storeId);
    if (row.status !== 'not_installed') continue;
    if (row.photoRequest) photoRequestNotInstalled += 1;
    if (!row.photoRequest && row.outcome === 'left_with_manager') realLeftWithManager += 1;
    if (!row.photoRequest && row.outcome === 'removed_from_store') realRemovedFromStore += 1;
    if (row.outcome !== 'left_with_manager' && row.outcome !== 'removed_from_store') continue;
    const reason = row.reason?.trim() || '(no reason)';
    const entry = reasons.get(reason) ?? { reason, leftWithManager: 0, removedFromStore: 0 };
    if (row.outcome === 'left_with_manager') entry.leftWithManager += 1;
    else entry.removedFromStore += 1;
    reasons.set(reason, entry);
  }

  return {
    planned: bucket.planned,
    stores: storeIds.size,
    submitted: bucket.installed + bucket.leftWithManager + bucket.removedFromStore + bucket.submittedWithZeroUnits,
    installed: bucket.installed,
    leftWithManager: bucket.leftWithManager,
    removedFromStore: bucket.removedFromStore,
    submittedWithZeroUnits: bucket.submittedWithZeroUnits,
    notYetRecorded: bucket.notYetRecorded,
    notInstalled: bucket.leftWithManager + bucket.removedFromStore + bucket.submittedWithZeroUnits,
    photoRequestNotInstalled,
    realLeftWithManager,
    realRemovedFromStore,
    reasons: [...reasons.values()].sort((a, b) =>
      (b.leftWithManager + b.removedFromStore) - (a.leftWithManager + a.removedFromStore)
      || a.reason.localeCompare(b.reason)),
  };
}

export function groupInstallStatusByCampaign(rows: readonly InstallStatusRow[]): InstallStatusCampaignGroup[] {
  const groups = new Map<number, InstallStatusCampaignGroup>();
  for (const row of rows) {
    const group = groups.get(row.campaignId) ?? {
      campaignId: row.campaignId,
      campaignName: row.campaignName,
      photoRequest: row.photoRequest,
      ...emptyBucket(),
    };
    group.photoRequest = group.photoRequest || row.photoRequest;
    addToBucket(group, row);
    groups.set(row.campaignId, group);
  }
  return [...groups.values()].sort((a, b) =>
    (b.leftWithManager - a.leftWithManager)
    || (b.removedFromStore - a.removedFromStore)
    || a.campaignName.localeCompare(b.campaignName));
}

export function groupInstallStatusByStore(rows: readonly InstallStatusRow[]): InstallStatusStoreGroup[] {
  const groups = new Map<number, InstallStatusStoreGroup & { operatorNames: Set<string> }>();
  for (const row of rows) {
    const group = groups.get(row.storeId) ?? {
      storeId: row.storeId,
      storeNo: row.storeNo,
      storeName: row.storeName,
      ...emptyBucket(),
      operators: '',
      operatorNames: new Set<string>(),
    };
    if (!group.storeNo && row.storeNo) group.storeNo = row.storeNo;
    addToBucket(group, row);
    if (row.status === 'not_installed' && row.operatorName) group.operatorNames.add(row.operatorName);
    groups.set(row.storeId, group);
  }
  return [...groups.values()]
    .sort((a, b) =>
      (b.leftWithManager - a.leftWithManager)
      || (b.removedFromStore - a.removedFromStore)
      || a.storeName.localeCompare(b.storeName))
    .map(({ operatorNames, ...group }) => ({
      ...group,
      operators: [...operatorNames].sort((a, b) => a.localeCompare(b)).join(', '),
    }));
}

export function filterInstallStatusRows(
  rows: readonly InstallStatusRow[],
  options: {
    campaignId: number | null;
    storeId: number | null;
    view: InstallStatusView;
    hidePhotoRequests: boolean;
  },
): InstallStatusRow[] {
  return rows
    .filter((row) => {
      if (options.campaignId != null && row.campaignId !== options.campaignId) return false;
      if (options.storeId != null && row.storeId !== options.storeId) return false;
      if (options.hidePhotoRequests && row.photoRequest) return false;
      switch (options.view) {
        case 'all':
          return true;
        case 'not_installed':
          return row.status === 'not_installed';
        case 'installed':
          return row.status === 'installed';
        case 'not_yet_recorded':
          return row.status === 'not_yet_recorded';
        default: {
          const unreachable: never = options.view;
          return unreachable;
        }
      }
    })
    .sort((a, b) =>
      a.storeName.localeCompare(b.storeName)
      || a.campaignName.localeCompare(b.campaignName)
      || a.taskId - b.taskId);
}

export function installStatusNarrative(summary: InstallStatusSummary): string {
  if (summary.submitted === 0) return 'No posting tasks have been recorded yet.';
  if (summary.notInstalled === 0) {
    return `Every submitted posting task in this view is installed. ${formatInstallStatusCount(summary.notYetRecorded)} are still to record.`;
  }
  return `${formatInstallStatusCount(summary.photoRequestNotInstalled)} of ${formatInstallStatusCount(summary.notInstalled)} not-installed tasks are photo requests. Real installs left with the manager: ${formatInstallStatusCount(summary.realLeftWithManager)}. Real installs removed from the store: ${formatInstallStatusCount(summary.realRemovedFromStore)}.`;
}

export function installStatusComparisonText(
  current: { cycleName: string; leftWithManager: number; submitted: number },
  previous: { cycleName: string; leftWithManager: number; submitted: number },
): string {
  const previousShare = formatInstallStatusPercent(previous.leftWithManager, previous.submitted) ?? '0.0%';
  const currentShare = formatInstallStatusPercent(current.leftWithManager, current.submitted) ?? '0.0%';
  const base = `${previous.cycleName} finished with ${formatInstallStatusCount(previous.leftWithManager)} left with the manager, ${previousShare} of ${formatInstallStatusCount(previous.submitted)} submitted posting tasks. ${current.cycleName} has ${formatInstallStatusCount(current.leftWithManager)} so far, ${currentShare} of ${formatInstallStatusCount(current.submitted)} submitted.`;
  if (current.leftWithManager > previous.leftWithManager) {
    return `${base} ${current.cycleName} has already passed the previous cycle total.`;
  }
  return base;
}

/** Calendar day of the cycle in Ireland, starting at 1 on the start date. */
export function installStatusCycleDay(startDate: string | Date, now: Date): number | null {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(now.getTime())) return null;
  const format = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const startUtc = Date.parse(`${format.format(start)}T00:00:00Z`);
  const nowUtc = Date.parse(`${format.format(now)}T00:00:00Z`);
  if (Number.isNaN(startUtc) || Number.isNaN(nowUtc)) return null;
  return Math.max(1, Math.floor((nowUtc - startUtc) / 86_400_000) + 1);
}
